// Lightweight slider-component used by the product media gallery.
// Registers only if no other definition exists.
(() => {
  if (customElements.get('slider-component')) return;

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[id^="Slider-"]');
      if (!this.slider) return;

      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.sliderControlWrapper = this.querySelector('.slider-buttons');
      this.currentPageElement = this.querySelector('.slider-counter--current');
      this.pageTotalElement = this.querySelector('.slider-counter--total');
      this.hasDots = false;

      if (this.sliderControlWrapper) {
        const dots = this.sliderControlWrapper.querySelectorAll('.slider-counter__link');
        if (dots.length) {
          this.sliderControlLinksArray = Array.from(dots);
          this.sliderControlLinksArray.forEach(link =>
            link.addEventListener('click', this.linkToSlide.bind(this))
          );
          this.hasDots = true;
        }
      }

      this.slider.addEventListener('scroll', this.update.bind(this));
      if (this.prevButton) this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      if (this.nextButton) this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

      this.initPages();
      const ro = new ResizeObserver(() => this.initPages());
      ro.observe(this.slider);
    }

    visibleItems() {
      return Array.from(this.sliderItems).filter(item => !item.classList.contains('hidden') && item.clientWidth > 0);
    }

    initPages() {
      this.sliderItemsToShow = this.visibleItems();
      if (this.sliderItemsToShow.length < 2) {
        this.totalPages = 1;
        this.update();
        return;
      }
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      if (!this.sliderItemOffset) this.sliderItemOffset = this.sliderItemsToShow[0].clientWidth;
      this.slidesPerPage = Math.max(
        1,
        Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset)
      );
      this.totalPages = Math.max(1, this.sliderItemsToShow.length - this.slidesPerPage + 1);
      this.update();
    }

    resetPages() {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.initPages();
    }

    update() {
      if (!this.slider) return;
      const prevPage = this.currentPage;
      this.currentPage =
        this.sliderItemOffset > 0
          ? Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1
          : 1;

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      if (this.currentPage !== prevPage) {
        this.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow && this.sliderItemsToShow[this.currentPage - 1]
            }
          })
        );
      }

      if (this.hasDots && this.sliderControlLinksArray) {
        let visibleCount = 0;
        const targetIndex = this.currentPage - 1;
        this.sliderControlLinksArray.forEach(link => {
          link.classList.remove('slider-counter__link--active');
          link.removeAttribute('aria-current');
          if (!link.classList.contains('hidden')) {
            if (visibleCount === targetIndex) {
              link.classList.add('slider-counter__link--active');
              link.setAttribute('aria-current', 'true');
            }
            visibleCount++;
          }
        });
      }

      if (this.prevButton && this.nextButton && this.sliderItemsToShow && this.sliderItemsToShow.length) {
        const first = this.sliderItemsToShow[0];
        const last = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
        if (this.isSlideVisible(first) && this.slider.scrollLeft === 0) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }
        if (this.isSlideVisible(last, true)) {
          this.nextButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextButton.removeAttribute('disabled');
        }
      }
    }

    isSlideVisible(item, isLast = false, offset = 0) {
      if (!item) return true;
      const itemRight = item.offsetLeft + item.clientWidth;
      const trackRight = this.slider.scrollLeft + this.slider.clientWidth + (isLast ? 10 : 0) - offset;
      return itemRight <= trackRight;
    }

    onButtonClick(e) {
      e.preventDefault();
      const step = parseInt(e.currentTarget.dataset.step, 10) || 1;
      const delta = step * (this.sliderItemOffset || this.slider.clientWidth);
      const target =
        e.currentTarget.name === 'next'
          ? this.slider.scrollLeft + delta
          : this.slider.scrollLeft - delta;
      this.slider.scrollTo({ left: target, behavior: 'smooth' });
    }

    linkToSlide(e) {
      e.preventDefault();
      const linkIndex = this.sliderControlLinksArray.indexOf(e.currentTarget);
      let hidden = 0;
      for (let i = 0; i < linkIndex; i++) {
        if (this.sliderControlLinksArray[i].classList.contains('hidden')) hidden++;
      }
      const visibleIndex = linkIndex - hidden;
      const offset = visibleIndex + 1 - this.currentPage;
      const firstSlide = this.sliderItemsToShow && this.sliderItemsToShow[0];
      const itemWidth = (firstSlide && firstSlide.clientWidth) || this.sliderItemOffset || this.slider.clientWidth;
      this.slider.scrollTo({
        left: this.slider.scrollLeft + itemWidth * offset,
        behavior: 'smooth'
      });
    }
  }

  customElements.define('slider-component', SliderComponent);
})();
